import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {ContentOpener} from '../ContentPopper/ContentOpener';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {Divider} from '../Divider/Divider';
import {Icon, IconSize} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';
import {Modal, ModalProps} from '../Modal/Modal';

export type ContentAlignment =
  | 'center'
  | 'baseline'
  | 'end'
  | 'start'
  | 'flex-start'
  | 'flex-end'
  | 'stretch';

export type ModalDialogProps = ContentPopperBaseProps &
  ModalProps & {
    contentAlignment?: ContentAlignment;
    openerAlignment?: ContentAlignment;
    /**
     * Modal grows to full width by default
     * Set to `true` to fit content and to disable auto grow
     */
    contentFitWidth?: boolean;
    contentTransparent?: boolean;
    disableCloseOnOverlayClick?: boolean;
    renderInPortal?: boolean;
    backgroundBlur?: boolean;
    fixedSmallWidth?: boolean;
    animated?: boolean;
  };

const CLOSE_ICON_SIZE = 'S';

export const ModalDialog: FC<ModalDialogProps> = ({
  headerContent,
  bodyContent,
  openerContent,
  onOpenerClick,
  open,
  isActive,
  showCloseIcon = true,
  onClose,
  openerAlignment,
  contentAlignment,
  contentFitWidth,
  contentTransparent = false,
  showHeaderDivider,
  className,
  renderInPortal,
  dataTestId = 'modalDialog',
  backgroundBlur,
  fixedSmallWidth = true,
  animated = true,
  onUnmount,
  zIndex,
}) => {
  const handleClose = (e: React.MouseEvent<SVGSVGElement | HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClose?.();
  };

  return (
    <>
      {openerContent ? (
        <StyledContentOpener
          isOpen={open}
          isActive={isActive}
          openerType={null}
          data-testid={dataTestId ? `${dataTestId}Opener` : 'opener'}
          onClick={onOpenerClick}
          $openerAlignment={openerAlignment}
        >
          {openerContent}
        </StyledContentOpener>
      ) : null}

      <StyledModal
        backgroundBlur={backgroundBlur}
        renderInPortal={renderInPortal}
        bodyContent={
          <ModalDialogContent
            id="modalContent"
            data-fixed-small-width={fixedSmallWidth}
            showCloseIcon={Boolean(headerContent) && showCloseIcon}
            contentAlignment={contentAlignment}
            data-testid={`${dataTestId}Content`}
            contentFitWidth={Boolean(contentFitWidth)}
            $contentTransparent={contentTransparent}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <ModalDialogHeaderContent leftPadding={showCloseIcon}>
              {headerContent}
              {showCloseIcon && (
                <ModalDialogCloseIcon
                  asset={GenericIconName.Close}
                  data-testid={`${dataTestId}CloseIcon`}
                  onClick={handleClose}
                  size={CLOSE_ICON_SIZE}
                  $floatingElement={Boolean(!headerContent)}
                />
              )}
            </ModalDialogHeaderContent>
            {headerContent && showHeaderDivider && <HeaderDivider />}
            {bodyContent}
          </ModalDialogContent>
        }
        showCloseIcon={false}
        onClose={onClose}
        onModalClick={handleClose}
        className={className}
        dataTestId={dataTestId}
        open={open}
        onUnmount={onUnmount}
        animated={animated}
        zIndex={zIndex}
      />
    </>
  );
};

const StyledContentOpener = styled(ContentOpener)<{
  $openerAlignment?: ContentAlignment;
}>`
  justify-content: ${props => props.$openerAlignment || 'unset'};
`;

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
`;

export const ModalDialogContent = styled.div<{
  showCloseIcon: boolean;
  contentAlignment?: ContentAlignment;
  contentFitWidth: boolean;
  $contentTransparent: boolean;
}>`
  padding-top: ${({showCloseIcon}) => (showCloseIcon ? '3px' : '0')};
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch; /* iOS Momentum Scroll */
  box-sizing: border-box;
  border-radius: 6px;
  min-width: 340px;
  max-width: 800px;
  max-height: calc(100% - 60px);
  background-clip: padding-box;
  align-self: ${({contentAlignment}) => contentAlignment || 'center'};
  position: relative;
  width: ${({contentFitWidth}) => (contentFitWidth ? 'auto' : '100%')};
  overflow: auto;
  background: ${({$contentTransparent}) =>
    $contentTransparent ? 'transparent' : COLORS.White};
  box-shadow: 0 0 75px 0 rgba(0, 0, 0, 0.1), 0 2px 30px 0 rgba(0, 0, 0, 0.02);
  z-index: 1;

  &[data-fixed-small-width='true'] {
    width: 340px;
    min-width: unset;
  }
`;

export const ModalDialogCloseIcon = styled(Icon)<{$floatingElement: boolean}>`
  position: ${props => (props.$floatingElement ? 'absolute' : '')};
  top: ${props => (props.$floatingElement ? '0' : '')};
  right: ${props => (props.$floatingElement ? '0' : '')};
  z-index: ${props => (props.$floatingElement ? '1' : '')};

  border: none;
  padding: 9px;
  cursor: pointer;
  background-color: transparent;
  display: flex;
  box-sizing: content-box;
`;

const HeaderDivider = styled(Divider)`
  margin: 1rem 0 1.5rem 0;
`;

export const ModalDialogHeaderContent = styled.div<{leftPadding?: boolean}>`
  display: flex;
  flex-direction: row;
  z-index: 1;
  padding-left: ${({leftPadding}) =>
    leftPadding ? `${IconSize[CLOSE_ICON_SIZE]}px` : 0};
`;
